import {
  getProductWizardRoute,
  listOwnBackendProductCodes,
} from '@/app/config/products.config';
import { useProducts } from '@/features/quotes/hooks/common/use-products.hook';
import {
  quotesQueryOptions,
  useQuotes,
} from '@/features/quotes/hooks/common/use-quotes.hook';
import type { SummaryQuotes } from '@/services/quotes/types';
import {
  DateRangeFilterField,
  SearchFilterField,
  SelectFilterField,
} from '@/shared/ui';
import { Page, PageHeader } from '@/shared/ui/page';
import { f, formatDateToBrazil, useFilters } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import {
  Avatar,
  DataTable,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { useQueries } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CalendarDays, SquarePen, User } from 'lucide-react';
import { useMemo } from 'react';
import {
  createAnalysesColumns,
  type AnalysisAction,
} from './analyses-table-columns';

const QUOTE_UNDER_ANALYSIS_STATUS = '17';

// A tela é escopada em "Cotação em Análise": o dropdown reflete o status da
// jornada, e "Todos" continua caindo no mesmo status.
const statusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Em Análise', value: QUOTE_UNDER_ANALYSIS_STATUS },
];

const DEFAULT_SORT_FIELD = 'index_time';
const DEFAULT_SORT_TYPE = 'DESC';

const ANALYSES_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  insuredName: 'Nome',
  brokerName: 'AgentName',
  productName: 'ProductName',
  proposalNumber: 'ProposalNo',
};

const ANALYSES_COLUMN_ID_BY_SORT_FIELD: Record<string, string> = {
  Nome: 'insuredName',
  AgentName: 'brokerName',
  ProductName: 'productName',
  ProposalNo: 'proposalNumber',
};

export function AnalysesContent() {
  const navigate = useNavigate();

  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    status: f.string().withDefault(''),
    productCode: f.string().withDefault(''),
    dateFrom: f.string().withDefault(''),
    dateTo: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
  });

  const { data: products } = useProducts();
  const productOptions = useMemo(
    () => [
      { label: 'Todos os produtos', value: '' },
      ...(products ?? []).map(product => ({
        label: product.name,
        value: product.code,
      })),
    ],
    [products]
  );

  const {
    data: quotes,
    isLoading,
    error,
  } = useQuotes({
    pageNumber: debouncedFilters.pageNo,
    pageSize: debouncedFilters.pageSize,
    sortType: filters.sortType === 'DESC' ? 'DESC' : 'ASC',
    sortField: filters.sortField,
    search: debouncedFilters.search,
    status: debouncedFilters.status || QUOTE_UNDER_ANALYSIS_STATUS,
    productCode: debouncedFilters.productCode,
    proposalDateFrom: debouncedFilters.dateFrom || undefined,
    proposalDateTo: debouncedFilters.dateTo || undefined,
  });

  // Produtos de backend próprio (registry, ex.: RD Equipamentos): as cotações
  // em análise deles não chegam pelo endpoint padrão de análise, que não
  // filtra por produto — o BFF só os traz quando o `productCode` deles é
  // enviado. Sem filtro de produto, buscamos cada um à parte e os injetamos na
  // lista; com filtro, a consulta principal já traz o produto filtrado (e só
  // ele). Volume por corretora é pequeno: busca uma página ampla.
  const ownBackendQuotes = useQueries({
    queries: listOwnBackendProductCodes().map(productCode => ({
      ...quotesQueryOptions({
        pageNumber: 1,
        pageSize: 100,
        sortType: 'DESC',
        sortField: 'index_time',
        search: debouncedFilters.search,
        status: QUOTE_UNDER_ANALYSIS_STATUS,
        productCode,
      }),
      enabled: !debouncedFilters.productCode,
    })),
  });

  const handleAction = (_action: AnalysisAction, quote: SummaryQuotes) => {
    const targetId = quote.policyId ?? quote.id;
    if (!targetId) return;
    // Produto de backend próprio abre no wizard dele (no passo que o status de
    // análise pede — no RD, CALCULATION, onde fica "Aprovar (subscrição)"),
    // não na tela de análise PME/Empresarial.
    const wizardRoute = getProductWizardRoute(
      quote.productCode,
      targetId,
      QUOTE_UNDER_ANALYSIS_STATUS
    );
    if (wizardRoute) {
      void navigate(wizardRoute);
      return;
    }
    void navigate({
      to: '/analyses/$policyId',
      params: { policyId: targetId },
    });
  };

  const columns = createAnalysesColumns(handleAction);

  // Os produtos de backend próprio vêm de consulta à parte (a busca de análise
  // não filtra por produto) e entram só na primeira página, para não duplicar
  // ao paginar.
  const ownBackendData = debouncedFilters.productCode
    ? []
    : ownBackendQuotes.flatMap(
        query => (query.data?.data ?? []) as SummaryQuotes[]
      );
  const apiData = (quotes?.data ?? []) as SummaryQuotes[];
  const data = filters.pageNo === 1 ? [...ownBackendData, ...apiData] : apiData;
  const totalPages = quotes?.meta?.totalPages ?? 0;
  const totalItems = (quotes?.meta?.total ?? 0) + ownBackendData.length;
  const sorting = useMemo(() => {
    const columnId = ANALYSES_COLUMN_ID_BY_SORT_FIELD[filters.sortField];

    if (!columnId) return [];

    return [
      {
        id: columnId,
        desc: filters.sortType === 'DESC',
      },
    ];
  }, [filters.sortField, filters.sortType]);

  return (
    <>
      <PageHeader.Filters>
        <div className='flex flex-col gap-3 md:flex-1 md:flex-row md:items-center md:gap-4 lg:max-w-4xl'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CNPJ, responsável ou email...'
            className='w-full'
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={statusOptions}
            placeholder='Status'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, status: value, pageNo: 1 })
            }
          />
          <SelectFilterField
            {...registerFilter('productCode')}
            items={productOptions}
            placeholder='Produto'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, productCode: value, pageNo: 1 })
            }
          />
          <DateRangeFilterField
            from={filters.dateFrom}
            to={filters.dateTo}
            placeholder='Selecione a data'
            className='w-full md:min-w-[200px]'
            onChange={range =>
              setFilters({
                ...filters,
                dateFrom: range.from,
                dateTo: range.to,
                pageNo: 1,
              })
            }
          />
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar cotações em análise. Tente novamente.
          </div>
        ) : (
          <>
            <div className='space-y-4 md:hidden'>
              {isLoading ? (
                Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                  (_, index) => (
                    <div
                      key={index}
                      className='h-60 animate-pulse rounded-3xl bg-alba-gray-200'
                    />
                  )
                )
              ) : data.length === 0 ? (
                <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  Nenhuma cotação em análise encontrada.
                </div>
              ) : (
                data.map(quote => {
                  const brokerName = quote.brokerName
                    ? quote.brokerName.split('\n')[0]
                    : '-';

                  return (
                    <article
                      key={quote.policyId || quote.id}
                      className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div className='flex min-w-0 items-center gap-3'>
                          <Avatar.Root
                            size='md'
                            shape='circle'
                            className='bg-alba-blue-500'
                          >
                            <User className='h-4 w-4 text-white' />
                          </Avatar.Root>
                          <div className='min-w-0'>
                            <p className='truncate font-gotham font-bold text-alba-black-500'>
                              {quote.insuredName}
                            </p>
                            <p className='mt-1 text-xs text-alba-gray-600'>
                              {formatRegisterNumber(quote.registerNumber)}
                            </p>
                          </div>
                        </div>
                        <Menu.Root>
                          <Menu.Trigger />
                          <Menu.Content>
                            <Menu.Item
                              value='analysis'
                              icon={<SquarePen size={16} strokeWidth={1.6} />}
                              onSelect={() => handleAction('analysis', quote)}
                            >
                              Análise
                            </Menu.Item>
                          </Menu.Content>
                        </Menu.Root>
                      </div>

                      <div className='mt-4 space-y-2'>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Corretor
                          </p>
                          <p className='truncate text-sm text-alba-gray-700'>
                            {brokerName}
                          </p>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Produto
                          </p>
                          <p className='truncate text-sm text-alba-gray-700'>
                            {quote.productName}
                          </p>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Nº da Cotação
                          </p>
                          <div className='flex flex-col items-end gap-0.5 text-right'>
                            <p className='text-sm text-alba-gray-700'>
                              {quote.proposalNumber}
                            </p>
                            <div className='flex items-center gap-1 text-xs text-alba-gray-600'>
                              <CalendarDays className='h-3.5 w-3.5' />
                              <span>
                                {formatDateToBrazil(quote.proposalDate)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Status
                          </p>
                          <Tag.Root
                            variant='outline'
                            colorScheme='yellow'
                            size='sm'
                          >
                            <Tag.Label>Em Análise</Tag.Label>
                          </Tag.Root>
                        </div>
                      </div>
                    </article>
                  );
                })
              )}

              {totalPages > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={totalPages * filters.pageSize}
                    page={filters.pageNo}
                    pageSize={filters.pageSize}
                    totalItems={totalItems}
                    pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        pageNo: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, pageNo: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
              <DataTable
                className='min-w-0'
                loading={isLoading}
                columns={columns}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                totalPages={totalPages}
                data={data}
                pageSize={filters.pageSize}
                totalItems={totalItems}
                manualSorting
                sorting={sorting}
                onSortChange={nextSorting => {
                  const nextSort = nextSorting[0];
                  const sortField = nextSort
                    ? ANALYSES_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                    : DEFAULT_SORT_FIELD;

                  setFilters({
                    ...filters,
                    pageNo: 1,
                    sortField: sortField ?? DEFAULT_SORT_FIELD,
                    sortType: nextSort?.desc ? 'DESC' : 'ASC',
                  });
                }}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, pageNo: page, pageSize })
                }
              />
            </div>
          </>
        )}
      </Page.Content>
    </>
  );
}
